import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useApproveRegistrationRequestMutation, useRegistrationRequestQuery } from "@/api/query-hooks";
import type { RegistrationRequestRecord } from "@/api/types";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDateTime } from "@/lib/date";
import { MonitoringApprovalAction } from "@/features/licensing/monitoring-approval-action";
import { ClientIdentity } from "@/features/licensing/client-identity";

export function RegistrationRequestDetailPage() {
  const { requestId } = useParams({ strict: false }) as { requestId: string };
  const { t, i18n } = useTranslation(["registrationRequests", "common"]);
  const request = useRegistrationRequestQuery(requestId);

  if (request.isLoading) {
    return <LoadingState />;
  }
  if (request.isError) {
    return <ErrorState title={t("common:states.error")} detail={safeRequestError(request.error)} onRetry={() => void request.refetch()} />;
  }
  if (!request.data) {
    return <EmptyState title={t("common:states.empty")} />;
  }

  return <RequestDetail request={request.data} language={i18n.language} onRefresh={() => void request.refetch()} />;
}

function RequestDetail({ request, language, onRefresh }: { request: RegistrationRequestRecord; language: string; onRefresh: () => void }) {
  const { t } = useTranslation("registrationRequests");
  const approve = useApproveRegistrationRequestMutation();
  const [stationNote, setStationNote] = useState(request.hostname ?? "");
  const relation = request.enrollmentRelation;
  const canOpenStation = request.persistedStatus === "CONSUMED" && request.effectiveStatus === "CONSUMED" &&
    relation?.tokenKind === "REGISTRATION_V1" && Boolean(relation.stationId);
  const canApprove = request.persistedStatus === "PENDING" && request.effectiveStatus === "PENDING" &&
    Number.isSafeInteger(request.clientId) && (request.clientId ?? 0) > 0 && request.revision > 0;

  const approveCurrent = async () => {
    if (!canApprove || !request.clientId) return;
    try {
      await approve.mutateAsync({
        requestId: request.id,
        clientId: request.clientId,
        expectedRevision: request.revision,
        stationNote
      });
      toast.success(t("approved"));
      onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("approvalFailed"));
      throw error;
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{t("detailTitle")}</h2>
          <p className="mt-1 break-all text-sm text-muted-foreground">{request.id}</p>
        </div>
        <Button variant="outline" onClick={onRefresh}><RefreshCw className="h-4 w-4" />{t("refresh")}</Button>
      </div>
      <section className="rounded-md border bg-panel p-4">
        <div className="mb-4 flex flex-wrap gap-2">
          <Badge variant="outline">{t("persistedStatus")}: {request.persistedStatus}</Badge>
          <Badge variant="outline">{t("effectiveStatus")}: {request.effectiveStatus}</Badge>
        </div>
        <dl className="grid gap-3 text-sm md:grid-cols-2">
          <Field label={t("appId")} value={request.appId} />
          <Field label={t("installationId")} value={request.installationId} />
          <Field label={t("clientId")} value={request.clientId ? <ClientIdentity clientId={request.clientId} link={false} /> : t("unbound")} />
          <Field label={t("hostname")} value={request.hostname || "—"} />
          <Field label={t("platform")} value={`${request.os}/${request.arch}`} />
          <Field label={t("version")} value={`${request.currentVersion} / ${request.releaseChannel}`} />
          <Field label={t("expiresAt")} value={formatDateTime(request.expiresAt, language)} />
          <Field label={t("updatedAt")} value={formatDateTime(request.updatedAt, language)} />
          {relation ? <Field label={t("tokenId")} value={relation.tokenId} /> : null}
          {relation ? <Field label={t("tokenKind")} value={relation.tokenKind} /> : null}
          {relation?.stationId ? <Field label={t("stationId")} value={relation.stationId} /> : null}
        </dl>
      </section>
      {canApprove ? (
        <section className="grid gap-3 rounded-md border bg-panel p-4">
          <div>
            <h3 className="font-medium">{t("approvalTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("approvalDescription")}</p>
          </div>
          <Label htmlFor="registration-request-station-note">{t("stationNote")}</Label>
          <Input
            id="registration-request-station-note"
            maxLength={2000}
            value={stationNote}
            onChange={(event) => setStationNote(event.target.value)}
          />
          <div>
            <MonitoringApprovalAction
              appId={request.appId}
              clientId={request.clientId ?? 0}
              relationKind="REGISTRATION_REQUEST"
              relationId={request.id}
              os={request.os}
              hostname={request.hostname || stationNote || request.installationId}
              approveLabel={t("approve")}
              disabled={approve.isPending}
              onApprove={approveCurrent}
            />
          </div>
        </section>
      ) : null}
      {canOpenStation && relation?.stationId ? (
        <section className="rounded-md border bg-panel p-4">
          <p className="text-sm text-muted-foreground">{t("consumedGuidance")}</p>
          <Button asChild className="mt-3">
            <Link
              to="/stations/$stationId"
              params={{ stationId: relation.stationId }}
              search={{ recoveryRequestId: request.id }}
            >
              {t("openLinkedStation")}<ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </section>
      ) : null}
      {request.effectiveStatus !== "CONSUMED" ? (
        <section className="rounded-md border bg-panel p-4 text-sm text-muted-foreground">
          {t(`state.${request.effectiveStatus.toLowerCase()}`)}
        </section>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return <div className="min-w-0"><dt className="text-muted-foreground">{label}</dt><dd className="break-all font-medium">{value}</dd></div>;
}

function safeRequestError(error: Error) {
  return error.message.includes("registration_request_not_found")
    ? "Registration request was not found or is not accessible."
    : "Registration request could not be loaded.";
}
